/**
 * AttributesSection - list + CRUD for `WorkflowAttributeDefinition`
 * (Rewrite §5.5).
 *
 * Attributes are the EAV replacement for per-action `capture_fields`:
 * the workflow owns a typed schema; actions pick a subset through the
 * `required_attrs` M2M at save-time. Rendered as a second section on
 * the Workflow General tab so admins can design the schema alongside
 * the label/description metadata.
 */

import { useMemo, useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import {
  useCreateWorkflowAttribute,
  useDeleteWorkflowAttribute,
  useWorkflowAttributes,
  type WorkflowAttributeDefinition,
} from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'
import {
  AttributeTemplatePicker,
  type AttributeTemplatePickResult,
  type AttributeTemplateResult,
} from '@/admin/components/shared/attribute-template-picker'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'

import { AttributeDialog } from './attribute-dialog'

interface AttributesSectionProps {
  workflowUuid: string
}

export function AttributesSection({ workflowUuid }: AttributesSectionProps) {
  const [editing, setEditing] = useState<WorkflowAttributeDefinition | null>(
    null,
  )
  const [showCreate, setShowCreate] = useState(false)
  const [prefillName, setPrefillName] = useState('')
  const [deleting, setDeleting] = useState<WorkflowAttributeDefinition | null>(null)
  const { data, isLoading } = useWorkflowAttributes(workflowUuid)
  const remove = useDeleteWorkflowAttribute(workflowUuid)
  const addAttr = useCreateWorkflowAttribute(workflowUuid)
  const resourceLookupProviders = useResourceLookupProviders()

  const attributes = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) =>
          a.order - b.order || a.display_name.localeCompare(b.display_name),
      ),
    [data],
  )

  async function handleDelete() {
    if (!deleting) return
    try {
      await remove.mutateAsync(deleting.uuid)
      toast.success('Attribute deleted')
      setDeleting(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete attribute')
    }
  }

  async function handleAddAttrPick(result: AttributeTemplatePickResult) {
    if (result.type === 'create') {
      setPrefillName(result.name ?? '')
      setShowCreate(true)
      return
    }
    if (result.type === 'existing') {
      const tpl: AttributeTemplateResult = result.template
      try {
        await addAttr.mutateAsync({
          code_name: tpl.code_name,
          display_name: tpl.display_name,
          description: tpl.description ?? '',
          field_type: tpl.field_type as WorkflowAttributeDefinition['field_type'],
          required: false,
          editable: true,
          configuration_data: tpl.configuration_data ?? {},
          hint: null,
          order: attributes.length,
        })
        toast.success('Attribute added')
      } catch (err) {
        toastApiError(err, 'Failed to add attribute')
      }
    }
  }

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
            Attributes
          </h3>
          <p className="text-[10px] text-[var(--ink-3)]">
            Typed fields captured on workflow instances. Actions pick the
            subset they require.
          </p>
        </div>
        <AttributeTemplatePicker
          onPick={handleAddAttrPick}
          resetAfterPick
          asAddButton
          domain="workflow"
          addButtonLabel="Add attribute"
          data-testid="workflows-add-attribute-picker"
        />
      </div>

      {isLoading ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
          Loading attributes…
        </div>
      ) : attributes.length === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
          No attributes yet. Add fields the workflow should capture.
        </div>
      ) : (
        <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
          {attributes.map((attr) => (
            <AttributeRow
              key={attr.uuid}
              attribute={attr}
              onEdit={() => setEditing(attr)}
              onDelete={() => setDeleting(attr)}
            />
          ))}
        </div>
      )}

      <AttributeDialog
        open={showCreate}
        onOpenChange={(next) => {
          setShowCreate(next)
          if (!next) setPrefillName('')
        }}
        mode="create"
        workflowUuid={workflowUuid}
        defaultOrder={attributes.length}
        prefillName={prefillName}
      />
      <AttributeDialog
        open={!!editing}
        onOpenChange={(next) => {
          if (!next) setEditing(null)
        }}
        mode="edit"
        workflowUuid={workflowUuid}
        attribute={editing ?? undefined}
      />
      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete ${deleting?.display_name ?? 'attribute'}?`}
        description="This removes the attribute from the workflow schema. Actions that capture it may need to be reviewed."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={remove.isPending}
        onConfirm={handleDelete}
      />
    </section>
    </ResourceLookupContextProvider>
  )
}

interface AttributeRowProps {
  attribute: WorkflowAttributeDefinition
  onEdit: () => void
  onDelete: () => void
}

function AttributeRow({ attribute, onEdit, onDelete }: AttributeRowProps) {
  return (
    <div
      className="flex items-center gap-2 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg)] px-3 py-2 first:border-t-0"
      data-testid={`workflows-attribute-row-${attribute.uuid}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[12px] text-[var(--ink)]">
            {attribute.display_name}
          </span>
          <Badge variant="outline" className="font-mono text-[10px]">
            {attribute.code_name}
          </Badge>
          <Badge variant="secondary" className="text-[10px] uppercase tracking-[0.08em]">
            {attribute.field_type.toLowerCase().replace(/_/g, ' ')}
          </Badge>
          {attribute.required && (
            <Badge
              variant="destructive"
              className="text-[10px] uppercase tracking-[0.08em]"
            >
              required
            </Badge>
          )}
          {!attribute.editable && (
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-[0.08em]"
            >
              read-only
            </Badge>
          )}
        </div>
        {attribute.hint && (
          <span className="truncate text-[10px] text-[var(--ink-3)]">
            {attribute.hint}
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onEdit}
          title="Edit attribute"
        >
          <Pencil size={13} />
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onDelete}
          title="Delete attribute"
        >
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  )
}
